import { ArrowCounterClockwiseIcon, CaretUpDownIcon, SignOutIcon } from '@phosphor-icons/react'
import { Crown } from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import type { ReactElement } from 'react'
import { Link } from 'react-router-dom'
import NameIcon from 'app/components/shared/NameIcon'
import { ThemeMenuItem } from 'app/components/sidebar/ThemeMenuItem'
import { Button } from 'app/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu'
import { useAuthStore } from 'app/stores/StoreContext'
import type { UserJson } from 'app/types/entities'
import { titleCase } from 'app/utils/titleCase'
import type { NavItemType } from 'types/Sidebar'

const ITEM_CLASS = `min-h-11 md:min-h-0`

export const UserMenu = observer(function UserMenu({
  user,
  items,
  initials,
  roleName,
}: {
  user: UserJson
  items: NavItemType[]
  initials: string
  roleName: string
}): ReactElement {
  const auth = useAuthStore()

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="row" data-testid="user-menu-trigger">
          <NameIcon
            fullName={initials}
            heightClassName="h-8"
            widthClassName="w-8"
            backgroundClassName="bg-fill-neutral-strong"
            textClassName="text-ui-sm leading-tight text-on-color"
            isTiny={true}
          />
          <span className="flex min-w-0 grow flex-col">
            <span className="truncate text-ui-sm text-strong">{user.name}</span>
            <span className="truncate text-label-xs capitalize text-subtle">{roleName}</span>
          </span>
          <CaretUpDownIcon size={16} weight="bold" className="shrink-0 text-subtle" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-(--radix-dropdown-menu-trigger-width)">
        {items.map((item) => {
          const Icon = item.icon
          return (
            <DropdownMenuItem key={item.name} asChild className={ITEM_CLASS}>
              <Link to={item.href ?? `#`}>
                {Icon && <Icon aria-hidden="true" />}
                <span className="grow">{titleCase(item.name)}</span>
                {item.superUserOnly && <Crown width={14} height={14} className="text-warning-strong" />}
              </Link>
            </DropdownMenuItem>
          )
        })}
        <ThemeMenuItem className={ITEM_CLASS} />
        {document.location.origin === `https://next.app.trackable.io` && (
          <DropdownMenuItem
            className={ITEM_CLASS}
            onSelect={() => {
              document.cookie = 'trackable-classic=1;max-age=43200;domain=.trackable.io;path=/;secure;samesite=lax'
              window.location.href = 'https://app.trackable.io'
            }}
          >
            <ArrowCounterClockwiseIcon aria-hidden="true" />
            Back to legacy
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator className="first:hidden" />
        <DropdownMenuItem
          className={ITEM_CLASS}
          onSelect={async () => {
            await auth.logout()
            // Hard redirect to a clean /login. A client-side navigate() races with
            // AppLayout's auth-redirect effect (which fires window.location.href to
            // /login?redirectTo=<authed path> once user becomes undefined), leaving
            // the user on a bounced URL.
            window.location.href = '/login'
          }}
        >
          <SignOutIcon aria-hidden="true" />
          Log out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
})
